@if (isLoading) {
  <app-loading-indicator [isLoading]="isLoading"></app-loading-indicator>
}

<div class="projectable-form-wrapper ipsec-tunneler-form">

  @if (model) {
    <lib-form-header
      [data]="model"
      [title]="'IPsec Tunneler: '"
      [badges]="bridgeHealthBadges"
      [moreActions]="[]"
      [showHeaderToggle]="false"
      [showHeaderButton]="false"
      (actionRequested)="headerActionRequested($event)"
    ></lib-form-header>
  }

  <div class="projectable-form-container ipsec-tunneler-container">

    @if (model && !isLoading) {

      <!-- Fixed-width main column. projectable-form-container has align-items: center,
           so bare flex children (tab nav) would shrink-to-content and center
           horizontally. Wrapping everything in a max-width column gives us a single
           anchored gutter that all the cards + tabs share. -->
      <div class="ipsec-main-column">

        @if (isStale && lastSeenLabel) {
          <div class="last-seen-row">last seen {{ lastSeenLabel }}</div>
        }

      <!-- Tab strip container is intentionally a <div>, NOT a <nav>: the cloud-ziti global
           styles.scss has an unscoped `nav { position: fixed; left: 0; width: 3.75rem; z-index: 26 }`
           rule targeting the bare element selector for the app-shell sidebar. -->
      <div class="tab-nav" role="tablist">
        <button type="button" role="tab" class="tab" [class.active]="activeTab === 'overview'" (click)="setTab('overview')">Overview</button>
        <button type="button" role="tab" class="tab" [class.active]="activeTab === 'ipsec'" (click)="setTab('ipsec')">IPsec</button>
        <button type="button" role="tab" class="tab" [class.active]="activeTab === 'metrics'" (click)="setTab('metrics')">Metrics</button>
      </div>

      <!-- ============================================================ -->
      <!-- Overview tab                                                  -->
      <!-- ============================================================ -->
      @if (activeTab === 'overview') {
        <!-- Two-column layout for the four info containers: data on the left,
             associated lists on the right. Collapses to a single column on
             narrower viewports (see .overview-columns SCSS @media rule). -->
        <div class="overview-columns">
          <div class="overview-column">
            <lib-form-field-container [title]="'NetFoundry Hosting'">
              <div class="field-grid">
                <div class="field-row"><div class="field-label">Status</div><span class="field-value">{{ model.status }}</span></div>
                <div class="field-row"><div class="field-label">Region</div><span class="field-value">{{ model.region }}</span></div>
                <div class="field-row"><div class="field-label">Bridge IP</div><span class="field-value">{{ model.host?.ipAddress || '—' }}</span></div>
                <div class="field-row">
                  <div class="field-label">Edge Router</div>
                  <span class="field-value">{{ model.name }}-router</span>
                </div>
              </div>

            </lib-form-field-container>

            <lib-form-field-container [title]="'Tunnel Info'">
              <div class="field-grid">
                <div class="field-row"><div class="field-label">Customer Public IP</div><span class="field-value">{{ model.customerPublicIp }}</span></div>
                <div class="field-row">
                  <div class="field-label">Customer Peer CIDRs</div>
                  <span class="field-value">{{ (model.customerPeerCidrs && model.customerPeerCidrs.length) ? model.customerPeerCidrs.join(', ') : '—' }}</span>
                </div>
                <div class="field-row"><div class="field-label">Last Heartbeat</div><span class="field-value">{{ formatEpochMs(bridgeHealth?.lastSeenAt) }}</span></div>
              </div>
            </lib-form-field-container>
          </div>

          <!-- Associated Services + Service Policies — mirror the identity-form
               widgets. The tunneler's ziti-endpoint identity is the join key;
               values come from ZitiDataService.getSubdata. Guarded on the identity
               being provisioned (zitiEndpointIdentityId set). -->
          @if (model.zitiEndpointIdentityId) {
            <div class="overview-column">
              <lib-form-field-container
                [title]="'Associated Services'"
                [count]="associatedServices.length"
                [class]="'hide-overflow'"
                [helpText]="'Preview list of all services this IPsec Tunneler\'s ziti identity is associated with.'"
              >
                <lib-preview-list
                  [clickable]="false"
                  [isLoading]="servicesLoading"
                  [allNames]="associatedServiceNames"
                ></lib-preview-list>
              </lib-form-field-container>

              <lib-form-field-container
                [title]="'Associated Service Policies'"
                [count]="associatedServicePolicies.length"
                [helpText]="'Preview list of all service policies this IPsec Tunneler\'s ziti identity is associated with.'"
              >
                <lib-preview-list
                  [clickable]="false"
                  [isLoading]="servicePoliciesLoading"
                  [allNames]="associatedServicePolicyNames"
                ></lib-preview-list>
              </lib-form-field-container>
            </div>
          }
        </div>

        <!-- Same frame as the explainer on the IPsec Tunnelers list page, with the live values of this tunnel. -->
        <app-flow-diagram
          class="overview-diagram"
          heading="How it works"
          storageKey="network-ipsec-tunneler-detail"
          [nodes]="overviewNodes"
          [links]="overviewLinks"
        ></app-flow-diagram>
      }

      <!-- ============================================================ -->
      <!-- IPsec tab                                                     -->
      <!-- ============================================================ -->
      @if (activeTab === 'ipsec') {
        <div class="tab-stack" [class.stale-content]="isStale">
          @if (isNeverReported) {
            <lib-form-field-container [title]="'Bridge Status'">
              <div class="empty-state-card">
                <h3>Waiting for first report</h3>
                <p>This tunneler hasn't reported metrics yet. Bridge VM may still be provisioning.</p>
                <p class="hint">Check back in a minute. This page auto-refreshes every 30s.</p>
              </div>
            </lib-form-field-container>
          }
          @if (!isNeverReported) {
            <lib-form-field-container [title]="'IPsec Snapshot'">
              <div class="section-row">
                <div class="field-grid bridge-snapshot">
                  <div class="field-row">
                    <div class="field-label">IKE State (Phase 1)</div>
                    <span class="field-value phase1-state phase-{{bridgeHealth?.ikeState}}">{{ bridgeHealth?.ikeState || 'UNKNOWN' }}</span>
                  </div>
                  <div class="field-row"><div class="field-label">IKEs Connected (Phase 1)</div><span class="field-value">{{ bridgeHealth?.ikesConnected ?? 0 }}</span></div>
                  <div class="field-row"><div class="field-label">IKE Established (Phase 1)</div><span class="field-value">{{ formatEpochMs(bridgeHealth?.ikeEstablishedAt) }}</span></div>
                  <div class="field-row"><div class="field-label">Bridge CPU</div><span class="field-value">{{ formatPct(bridgeHealth?.cpuPct) }}</span></div>
                  <div class="field-row"><div class="field-label">Bridge Memory</div><span class="field-value">{{ formatPct(bridgeHealth?.memPct) }}</span></div>
                  <div class="field-row"><div class="field-label">Last Seen</div><span class="field-value">{{ formatEpochMs(bridgeHealth?.lastSeenAt) }}</span></div>
                </div>
                <!-- Restart SA button hidden for the POC: the SDS --tags restart-sa path
                     isn't taking effect on the bridge VM (swanctl --terminate isn't
                     observable in charon logs even though the chain is wired end-to-end).
                     Re-add this button once that's debugged; backend (controller,
                     process, SDS, role's restart_sa.yml) is intact and ready to use. -->
              </div>
            </lib-form-field-container>

            <lib-form-field-container [title]="'Child SAs (Phase 2)'">
              @if (hasChildSAs()) {
                <table class="child-sa-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>State</th>
                      <th>Bytes In</th>
                      <th>Bytes Out</th>
                      <th>Established</th>
                      <th>Next Rekey</th>
                      <th>Last In (s)</th>
                      <th>Last Out (s)</th>
                      <th>Local TS</th>
                      <th>Remote TS</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (sa of bridgeHealth?.childSAs; track sa.childId) {
                      <tr>
                        <td>{{ sa.childName }}</td>
                        <td>{{ sa.state }}</td>
                        <td>{{ formatBytes(sa.bytesIn) }}</td>
                        <td>{{ formatBytes(sa.bytesOut) }}</td>
                        <td>{{ formatEpochMs(sa.establishedAt) }}</td>
                        <td>{{ formatEpochMs(sa.rekeyAt) }}</td>
                        <td>{{ sa.lastInboundSeconds ?? '—' }}</td>
                        <td>{{ sa.lastOutboundSeconds ?? '—' }}</td>
                        <td>{{ (sa.localTrafficSelectors || []).join(', ') || '—' }}</td>
                        <td>{{ (sa.remoteTrafficSelectors || []).join(', ') || '—' }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              }
              @if (!hasChildSAs()) {
                <div class="empty-state">No child SAs (Phase 2) reported.</div>
              }
            </lib-form-field-container>

            <lib-form-field-container [title]="'Bridge Logs'">
              <div class="events-controls">
                <span class="metrics-label">Window:</span>
                <div class="btn-group">
                  <button type="button" class="btn-group-item" [class.active]="eventsWindow === '1h'" (click)="setEventsWindow('1h')">1h</button>
                  <button type="button" class="btn-group-item" [class.active]="eventsWindow === '24h'" (click)="setEventsWindow('24h')">24h</button>
                  <button type="button" class="btn-group-item" [class.active]="eventsWindow === '7d'" (click)="setEventsWindow('7d')">7d</button>
                </div>
                <span class="metrics-label kind-label">Kind:</span>
                <div class="btn-group">
                  <button type="button" class="btn-group-item" [class.active]="eventKindFilter === null"
                          (click)="setEventKindFilter(null)">All</button>
                  @for (k of eventKinds; track k) {
                    <button type="button" class="btn-group-item" [class.active]="eventKindFilter === k"
                            (click)="setEventKindFilter(k)">{{ k }}</button>
                  }
                </div>
                <app-refresh-controls
                  subject="logs"
                  [loading]="eventsLoading"
                  [seconds]="eventsRefreshSeconds"
                  (refresh)="refreshEvents()"
                  (secondsChange)="setEventsRefresh($event)"
                />
              </div>
              <div class="log-search">
                <button type="button" class="log-search-go"
                        (click)="applyEventSearch()"
                        aria-label="Search logs">
                  <i class="pi pi-search" aria-hidden="true"></i>
                </button>
                <input
                  type="text"
                  class="form-field-input log-search-input"
                  [(ngModel)]="eventSearchInput"
                  (keydown.enter)="$event.preventDefault(); applyEventSearch()"
                  placeholder="Filter log messages by word or phrase"
                  aria-label="Search log messages"
                />
                @if (eventSearch) {
                  <button type="button" class="log-search-clear"
                          (click)="clearEventSearch()"
                          aria-label="Clear search">&times;</button>
                }
              </div>
              @if (events.length > 0) {
                <table class="events-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Kind</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (e of pagedEvents; track $index) {
                      <tr class="event-row">
                        <td>{{ formatEpochMs(e.t) }}</td>
                        <td>{{ e.kind }}</td>
                        <td class="event-message">{{ e.message }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
                <div class="events-pager">
                  <button type="button" class="pager-btn" (click)="prevEventsPage()" [disabled]="eventsPageIndex === 0">Prev</button>
                  <span class="pager-label">Page {{ eventsPageIndex + 1 }} of {{ eventsTotalPages }} ({{ events.length }} events)</span>
                  <button type="button" class="pager-btn" (click)="nextEventsPage()" [disabled]="eventsPageIndex >= eventsTotalPages - 1">Next</button>
                </div>
              }
              @if (!eventsLoading && events.length === 0) {
                <div class="empty-state">
                  No events in this range{{ eventKindFilter ? ' for kind "' + eventKindFilter + '"' : '' }}.
                </div>
              }
            </lib-form-field-container>
          }
        </div>
      }

      <!-- ============================================================ -->
      <!-- Metrics tab                                                   -->
      <!-- ============================================================ -->
      @if (activeTab === 'metrics') {
        <lib-form-field-container [title]="'Metrics'">
          <div class="metrics-controls">
            <span class="metrics-label">Window:</span>
            <div class="btn-group">
              <button type="button" class="btn-group-item" [class.active]="metricsWindow === '1h'" (click)="setMetricsWindow('1h')">1h</button>
              <button type="button" class="btn-group-item" [class.active]="metricsWindow === '24h'" (click)="setMetricsWindow('24h')">24h</button>
              <button type="button" class="btn-group-item" [class.active]="metricsWindow === '7d'" (click)="setMetricsWindow('7d')">7d</button>
            </div>
            @if (metrics) {
              <span class="generated-at">as of {{ formatEpochMs(metrics.generatedAt) }}</span>
            }
            <app-refresh-controls
              subject="metrics"
              [loading]="metricsLoading"
              [seconds]="metricsRefreshSeconds"
              (refresh)="refreshMetrics()"
              (secondsChange)="setMetricsRefresh($event)"
            />
          </div>
          @if (metricsLoading) {
            <app-loading-indicator [isLoading]="metricsLoading"></app-loading-indicator>
          }
          @if (!metricsLoading && metrics) {
            <div class="charts-grid">
              <div class="chart-card">
                <div class="chart-title">Throughput (rx / tx)</div>
                <app-rx-tx-timeline [samples]="rxTxSamples" [downtimeRanges]="metrics.downtimeRanges"
                                    [markers]="rekeyMarkers"
                                    height="14rem" unit="bytes" rxLabel="Rx" txLabel="Tx"></app-rx-tx-timeline>
                <div class="chart-footnote">Red bands mark IKE_SA (Phase 1) outages; amber dashed lines mark CHILD_SA (Phase 2) rekey moments (hover for the child id). Brief line gaps without either marker are scrape misses.</div>
              </div>
              <div class="chart-card">
                <div class="chart-title">Liveness (seconds since last in / out)</div>
                <app-rx-tx-timeline [samples]="livenessSamples" [downtimeRanges]="metrics.downtimeRanges"
                                    [markers]="rekeyMarkers"
                                    height="12rem" unit="seconds" rxLabel="In" txLabel="Out"></app-rx-tx-timeline>
              </div>
              <div class="chart-card">
                <div class="chart-title">Bridge (cpu / mem %)</div>
                <app-rx-tx-timeline [samples]="systemSamples" [downtimeRanges]="metrics.downtimeRanges"
                                    [markers]="rekeyMarkers"
                                    height="10rem" unit="percent" rxLabel="CPU" txLabel="Mem"></app-rx-tx-timeline>
              </div>
            </div>
          }
          @if (!metricsLoading && !metrics) {
            <div class="empty-state">No metrics data yet.</div>
          }
        </lib-form-field-container>
      }

      </div><!-- /.ipsec-main-column -->
    }
  </div>
</div>
